import styled from 'styled-components'

import { COLORS } from '../../commons/Colors'
import SwitchButtonComponent from '../../commons/components/SwitchButtonComponent'
import { UserOption } from '../API/FeatureFlagsAPI'
import { PlatformUsage } from '../flagCatalog'
import RoleSelector from './RoleSelector'
import UserListSelector from './UserListSelector'

export type PlatformConfig = {
    enabled: boolean
    disabledForUsers: string[]
    disabledForRoles: string[]
}

type Props = {
    title: string
    allUsers: UserOption[]
    value: PlatformConfig
    onChange: (value: PlatformConfig) => void
    usage?: PlatformUsage
}

export default function PlatformBlock({
    title,
    allUsers,
    value,
    onChange,
    usage,
}: Props) {
    const notApplicable = usage ? !usage.applies : false
    const effectNote = usage?.effect ? (
        <EffectNote>{usage.effect}</EffectNote>
    ) : null

    const renderBody = () => {
        if (notApplicable) {
            return <HelperNote>Esta flag não afeta o {title}.</HelperNote>
        }

        if (!value.enabled) {
            return (
                <>
                    {effectNote}
                    <HelperNote>
                        Plataforma indisponível para todos. Ative para
                        configurar exceções por usuário ou role.
                    </HelperNote>
                </>
            )
        }

        return (
            <>
                {effectNote}
                <Field>
                    <UserListSelector
                        label="Usuários sem acesso"
                        helperText="Deixe vazio para liberar a todos nesta plataforma"
                        allUsers={allUsers}
                        selectedIds={value.disabledForUsers}
                        onChange={(disabledForUsers) =>
                            onChange({ ...value, disabledForUsers })
                        }
                    />
                </Field>

                <Field>
                    <RoleSelector
                        label="Roles sem acesso"
                        selectedRoles={value.disabledForRoles}
                        onRoleAdd={(roleId) =>
                            onChange({
                                ...value,
                                disabledForRoles: [
                                    ...value.disabledForRoles,
                                    roleId,
                                ],
                            })
                        }
                        onRoleRemove={(roleId) =>
                            onChange({
                                ...value,
                                disabledForRoles: value.disabledForRoles.filter(
                                    (id) => id !== roleId
                                ),
                            })
                        }
                    />
                </Field>
            </>
        )
    }

    return (
        <Section data-testid={`platform-block-${title}`}>
            <SectionHeader>
                <SectionTitle>{title}</SectionTitle>
                <SwitchButtonComponent
                    active={value.enabled}
                    onClick={(enabled) => onChange({ ...value, enabled })}
                    disabled={notApplicable}
                    hideLabel
                    testid={`platform-enabled-${title}`}
                />
            </SectionHeader>

            {renderBody()}
        </Section>
    )
}

const Section = styled.div`
    border: 1px solid ${COLORS.borderGray};
    border-radius: 4px;
    padding: 14px;
    margin-bottom: 16px;
    background: #fafafa;
`

const SectionHeader = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 14px;
`

const SectionTitle = styled.span`
    display: block;
    font-size: 13px;
    font-weight: bold;
    color: ${COLORS.primaryGray};
`

const HelperNote = styled.p`
    margin: 0;
    font-size: 12px;
    color: ${COLORS.primaryGray};
    font-family: Roboto;
    line-height: 1.5;
`

const EffectNote = styled.p`
    margin: 0 0 12px 0;
    font-size: 12px;
    color: ${COLORS.primaryGray};
    font-family: Roboto;
    line-height: 1.5;
    font-style: italic;
`

const Field = styled.div`
    margin-bottom: 16px;

    &:last-child {
        margin-bottom: 0;
    }
`
